import { XIcon } from 'lucide-react'
import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'
import { cn } from '~/src/lib/utils'

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
Drawer.displayName = 'Drawer'

const DrawerTrigger = DrawerPrimitive.Trigger as typeof DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal as typeof DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close as typeof DrawerPrimitive.Close

const DrawerOverlay: React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Overlay>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 bg-black/50', className)}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  header?: React.ReactNode
  hideNotch?: boolean
  hideOverlay?: boolean
  showXClose?: boolean
  overlayClassName?: string
  notchClassName?: string
  adjustForSidebar?: boolean
}

const DrawerContent: React.ForwardRefExoticComponent<
  DrawerContentProps &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Content>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      header,
      hideNotch,
      hideOverlay,
      showXClose,
      overlayClassName,
      notchClassName,
      adjustForSidebar,
      ...props
    },
    ref
  ) => (
    <>
      <DrawerPortal>
        {!hideOverlay && (
          <DrawerOverlay
            className={overlayClassName}
            data-testid='drawer-overlay'
          >
            {header}
          </DrawerOverlay>
        )}
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            'bg-background fixed inset-x-0 bottom-0 z-50 flex h-auto !select-auto flex-col overflow-hidden rounded-t-[25px]',
            {
              'md:left-[var(--sidebar-width-collapsed)]': adjustForSidebar,
            },
            className
          )}
          {...props}
        >
          {children}

          {!hideNotch && (
            <div
              className={cn(
                'fixed inset-x-0 top-0 z-10 flex items-center justify-center bg-white p-3.5 md:hidden dark:bg-black',
                notchClassName
              )}
            >
              <div className='h-[5px] w-[30px] rounded-full bg-current opacity-20' />
            </div>
          )}

          {showXClose && (
            <DrawerClose className='absolute right-2 top-2 z-50 p-2 opacity-40'>
              <XIcon className='size-5 md:size-6' />
            </DrawerClose>
          )}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    </>
  )
)
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 text-center sm:text-left', className)}
    {...props}
  />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle: React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Title>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription: React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Description>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm opacity-60', className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
